import { atom } from "jotai";
import {
    allPendingBookingsAtom,
    visibleBookingsAtom,
    allActiveLocationsAtom,
    isMbxUserProvider
} from "./atoms";
import moment from "moment";

export const TAB_OPTIONS = {
    TODAY: "today",
    UPCOMING: "upcoming",
    PAST: "past"
};

export const ROLE_OPTIONS = {
    ALL: "all",
    ACTIVE: "active",
    INACTIVE: "inactive"
};

export const BOOKING_STATUS_OPTIONS = {
    ARRIVED: "Arrived",
    CANCELLED: "Cancelled",
    COMPLETED: "Completed",
    DECLINED: "Declined",
    EXPIRED: "Expired",
    NOSHOW: "No-show",
    PENDING: "Pending",
    SCHEDULED: "Scheduled"
};

export const selectedClientAtom = atom(null);
export const selectedProviderAtom = atom(null);

export const bookingPageSnackbarAtom = atom({
    open: false,
    message: "",
    severity: "success"
});
export const openManageBookingDialogAtom = atom(false);
export const futureBookingsNextTokenAtom = atom(null);
export const pastBookingsNextTokenAtom = atom(null);
export const selectedBookingIdAtom = atom(null);
export const selectedBookingAtom = atom(
    (get) => {
        const allPendingBookings = get(allPendingBookingsAtom);
        const visibleBookings = get(visibleBookingsAtom);
        const selectedBookingId = get(selectedBookingIdAtom);

        if (selectedBookingId === null) {
            return null;
        }

        // Try to find the booking in the pending bookings first
        let booking = allPendingBookings.find(
            (booking) => booking.id === selectedBookingId
        );

        // If not found in pending bookings, try to find in non pending bookings
        if (!booking) {
            booking = visibleBookings.find(
                (booking) => booking.id === selectedBookingId
            );
        }
        return booking || null;
    },
    (get, set, newBooking) => {
        if (newBooking?.id) {
            set(selectedBookingIdAtom, newBooking.id);
        }

        set(visibleBookingsAtom, (prev) => {
            const updatedBookings = prev.map((booking) =>
                booking.id === newBooking.id
                    ? { ...booking, ...newBooking }
                    : booking
            );
            if (
                !updatedBookings.some((booking) => booking.id === newBooking.id)
            ) {
                updatedBookings.push(newBooking);
            }
            return updatedBookings;
        });
    }
);
export const selectedBookingNotesContentAtom = atom((get) => {
    const selectedBooking = get(selectedBookingAtom);
    if (selectedBooking?.Notes) {
        return {
            createdAt: selectedBooking.createdAt,
            createdBy: `${selectedBooking.client.user.firstname} ${selectedBooking.client.user.lastname}`,
            updatedAt: selectedBooking.updatedAt,
            updatedBy: `${selectedBooking.client.user.firstname} ${selectedBooking.client.user.lastname}`,
            notes: selectedBooking.Notes[0].notes
        };
    } else if (selectedBooking?.order?.Notes) {
        const noteDialogContent = JSON.parse(selectedBooking.order.Notes);
        return noteDialogContent[0];
    } else {
        return "";
    }
});
export const selectedBookingNotesAtom = atom((get) => {
    const bookingNotesContent = get(selectedBookingNotesContentAtom);
    return bookingNotesContent?.notes || "";
});

export const overrideBookingFormDataAtom = atom({});
export const selectedTabAtom = atom(TAB_OPTIONS.TODAY);

export const tableConfigDefaultStatusAtom = atom((get) => {
    const isProviderLoggedIn = get(isMbxUserProvider);

    return isProviderLoggedIn
        ? ["Scheduled", "Arrived", "Completed", "Pending"]
        : ["Scheduled", "Arrived", "Completed"];
});

// Readable atom to hold the table configuration data
export const tableConfigBaseAtom = atom({
    statusFilter: [],
    roleFilter: ROLE_OPTIONS.ALL,
    dateFilter: {
        startDate: null,
        endDate: null
    },
    sort: { sortBy: null, direction: null }
});

// Writable atom for setting new config
export const tableConfigAtom = atom(
    (get) => {
        // Read from the base atom and also inject the dynamic statusFilter
        const tableConfig = get(tableConfigBaseAtom);
        const statusFilter = get(tableConfigBaseAtom).statusFilter.length
            ? get(tableConfigBaseAtom).statusFilter
            : get(tableConfigDefaultStatusAtom);

        return {
            ...tableConfig,
            statusFilter
        };
    },
    (get, set, newTableConfig) => {
        set(tableConfigBaseAtom, {
            ...get(tableConfigBaseAtom),
            ...newTableConfig
        });

        set(setPageAtom, 0);
    }
);

export const paginationAtom = atom({ page: 0, perPage: 25 });
export const setPageAtom = atom(null, (_get, set, page) => {
    set(paginationAtom, (prev) => ({ ...prev, page }));
});
export const setPerPageAtom = atom(null, (_get, set, perPage) => {
    set(paginationAtom, (prev) => ({ ...prev, perPage }));
});

export const openAddBookingDialogAtom = atom(false);

export const formatBookingsAtom = atom((get) => {
    const pendingBookings = get(allPendingBookingsAtom);
    const bookings = get(visibleBookingsAtom);
    const allLocations = get(allActiveLocationsAtom);

    const formattedPendingBookings = formatPendingBookingsHelper(
        pendingBookings,
        allLocations
    );
    const formattedBookings = formatBookingsHelper(bookings, allLocations);
    return [...formattedPendingBookings, ...formattedBookings];
});

const formatPendingBookingsHelper = (payload, allLocations) => {
    return payload.map(
        ({
            id,
            data: {
                serviceType: { name: serviceName = "" },
                address: location = "",
                isVirtual = false,
                bookingTz: locationTimezone = "",
                sdt: orderType = "",
                cancelledBy = null,
                clientNotes = "",
                client: {
                    id: clientId = "",
                    defaultpartialcc: clientDefaultPartialCC = "",
                    clientRegistered = true,
                    user: {
                        id: clientUserId = "",
                        active: isClientActive = true,
                        firstname: clientFirstName = "",
                        lastname: clientLastName = "",
                        emailaddress: clientEmail = "",
                        addressoneline: clientAddress = "",
                        mobilephone: clientPhoneNumber = "",
                        homePhone: clientHomePhone = "",
                        role: userRole = ""
                    }
                },
                clientpackage = {},
                origProvider: {
                    vmlink: origProviderVmlink = "",
                    id: origProviderId = "",
                    userId: origProviderUserId = "",
                    firstname: origProviderFirstName = "",
                    lastname: origProviderLastName = "",
                    emailaddress: origProviderEmail = "",
                    phone: origProviderPhoneNumber = ""
                },
                provider: {
                    vmlink: providerVmlink = "",
                    id: providerId = "",
                    userId = "",
                    firstname: providerFirstName = "",
                    lastname: providerLastName = "",
                    emailaddress: providerEmail = "",
                    phone: providerPhoneNumber = ""
                },
                selectedslot: {
                    datetime: startdate = Date.now(),
                    scheduleId = ""
                },
                cbu: { total = "" }
            },
            orderId = "",
            orderNotes = "",
            rrule = "",
            Notes: bookingNotes = "",
            status = "REQUESTED",
            totalAddChg = ""
        }) => {
            // Determine the correct provider data to return based on the presence of providerId, if no providerId defualt to original provider
            const providerData = providerId
                ? {
                      vmlink: providerVmlink,
                      providerId,
                      userId,
                      providerFirstName,
                      providerLastName,
                      providerEmail,
                      providerPhoneNumber,
                      providerTimezone: locationTimezone
                  }
                : {
                      vmlink: origProviderVmlink,
                      providerId: origProviderId,
                      userId: origProviderUserId,
                      providerFirstName: origProviderFirstName,
                      providerLastName: origProviderLastName,
                      providerEmail: origProviderEmail,
                      providerPhoneNumber: origProviderPhoneNumber,
                      providerTimezone: locationTimezone
                  };

            // Format the date and time
            const bookingDate = moment(startdate);
            const formattedDateWithYear =
                bookingDate.format("ddd, MMM D, YYYY");
            const formattedDateWithoutYear = bookingDate.format("ddd, MMM D");
            const formattedTime = bookingDate.format("h:mm A");
            const timezone = bookingDate
                .tz(providerData.providerTimezone)
                .format("z");
            const providerDateAndTime = {
                formattedDateWithYear: formattedDateWithYear,
                formattedDateWithoutYear: formattedDateWithoutYear,
                formattedTime: formattedTime,
                timezone: timezone
            };

            const cancelledByRole = cancelledBy?.role
                ? cancelledBy.role === "PROVIDER"
                    ? "provider"
                    : cancelledBy.role === "CLIENT"
                    ? "client"
                    : "admin"
                : "";

            const packageInitialQuantity = clientpackage?.initialQuantity
                ? clientpackage.initialQuantity
                : "";

            // Extract company location ID from scheduleId if |CL- is present
            let companyLocationId = "";
            let companyLocationName = "";

            if (!isVirtual && scheduleId.includes("|CL-")) {
                const companyLocationMatch = scheduleId
                    .split("|CL-")[1]
                    ?.split("|")[0];

                companyLocationId = companyLocationMatch || "";

                if (companyLocationId) {
                    const location = allLocations.find(
                        (location) => location.id === companyLocationId
                    );

                    if (location) {
                        companyLocationName = location.locationname;
                    }
                }
            }

            return {
                id,
                serviceName,
                location,
                companyLocationName,
                locationTimezone,
                isVirtual,
                orderType,
                orderId,
                rrule,
                cancelledByRole,
                client: {
                    clientId,
                    clientUserId,
                    isClientActive,
                    clientFirstName,
                    clientLastName,
                    clientEmail,
                    clientAddress,
                    clientPhoneNumber,
                    clientHomePhone,
                    clientDefaultPartialCC,
                    clientNotes,
                    clientRegistered,
                    userRole
                },
                packageInitialQuantity,
                provider: providerData,
                providerDateAndTime,
                bookingNotes,
                orderNotes,
                status,
                total,
                totalAddChg
            };
        }
    );
};

const formatBookingsHelper = (payload, allLocations) => {
    return payload.map(
        ({
            id,
            desc: serviceName = "",
            location = "",
            startdate = Date.now(),
            status = "SCHEDULED",
            timeblockid = "",
            timezone: locationTimezone,
            isVirtual = false,
            orderType = "",
            totalAddChg,
            Notes: bookingNotes = "",
            cancelledBy = null,
            client: {
                Notes: clientNotes = "",
                defaultpartialcc: clientDefaultPartialCC = "",
                id: clientId = "",
                userId: clientUserId = "",
                user: {
                    active: isClientActive = true,
                    firstname: clientFirstName = "",
                    lastname: clientLastName = "",
                    emailaddress: clientEmail = "",
                    addressoneline: clientAddress = "",
                    mobilephone: clientPhoneNumber = "",
                    homePhone: clientHomePhone = null,
                    registered: clientRegistered = true,
                    role: userRole = ""
                }
            },
            provider: {
                vmlink = "",
                id: providerId = "",
                userId = "",
                firstname: providerFirstName = "",
                lastname: providerLastName = "",
                emailaddress: providerEmail = "",
                phone: providerPhoneNumber = "",
                timezone: providerTimezone = ""
            },
            order: { id: orderId = "", total, Notes: orderNotes = "" }
        }) => {
            // If the location timzone is undefined, fallback to the provider timzone
            locationTimezone = locationTimezone ?? providerTimezone;
            // Format the date and time
            const bookingDate = moment(startdate);
            const formattedDateWithYear =
                bookingDate.format("ddd, MMM D, YYYY");
            const formattedDateWithoutYear = bookingDate.format("ddd, MMM D");
            const formattedTime = bookingDate.format("h:mm A");
            const timezone = bookingDate.tz(providerTimezone).format("z");
            const providerDateAndTime = {
                formattedDateWithYear: formattedDateWithYear,
                formattedDateWithoutYear: formattedDateWithoutYear,
                formattedTime: formattedTime,
                timezone: timezone
            };

            const cancelledByRole = cancelledBy?.role
                ? cancelledBy.role === "PROVIDER"
                    ? "provider"
                    : cancelledBy.role === "CLIENT"
                    ? "client"
                    : cancelledBy.role === "MARKETBOX_ADMIN"
                    ? "MarketBox admin"
                    : "admin"
                : "";

            // Extract company location ID from timeblockid if |CL- is present
            let companyLocationId = "";
            let companyLocationName = "";

            if (!isVirtual && timeblockid.includes("|CL-")) {
                const companyLocationMatch = timeblockid
                    .split("|CL-")[1]
                    ?.split("|")[0];

                companyLocationId = companyLocationMatch || "";

                if (companyLocationId) {
                    const location = allLocations.find(
                        (location) => location.id === companyLocationId
                    );

                    if (location) {
                        companyLocationName = location.locationname;
                    }
                }
            }

            return {
                id,
                serviceName,
                location,
                companyLocationName,
                locationTimezone,
                isVirtual,
                orderType,
                orderId,
                cancelledByRole,
                client: {
                    clientId,
                    clientUserId,
                    isClientActive,
                    clientFirstName,
                    clientLastName,
                    clientEmail,
                    clientAddress,
                    clientPhoneNumber,
                    clientHomePhone,
                    clientDefaultPartialCC,
                    clientNotes,
                    clientRegistered,
                    userRole
                },
                provider: {
                    vmlink,
                    providerId,
                    userId,
                    providerFirstName,
                    providerLastName,
                    providerEmail,
                    providerPhoneNumber,
                    providerTimezone
                },
                providerDateAndTime,
                bookingNotes,
                orderNotes,
                status,
                total,
                totalAddChg
            };
        }
    );
};

export const visibleRows = atom((get) => {
    const allFormattedBookings = get(formatBookingsAtom);

    return allFormattedBookings;
});
export const visibleRowsPaginated = atom((get) => {
    const { page, perPage } = get(paginationAtom);
    const visible = get(visibleRows);
    return visible.slice(perPage * page, perPage * (page + 1));
});
